import { useState } from 'react'

import { Box, Button, Icon, Link, Text } from '@factorialco/deprecated-design-system'
import { AnimatePresence } from 'framer-motion'
import {
  ReconciliationSuggestionInvoiceFragment,
  ReconciliationSuggestionPaymentExecutionFragment,
  TransactionReconciliationQuery,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToUI, dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { Carousel } from 'modules/banking/components/NewInvoiceReconciliationForm/ReconciliationInvoiceSuggestions/Carousel'
import { CarouselFooter } from 'modules/banking/components/NewInvoiceReconciliationForm/ReconciliationInvoiceSuggestions/CarouselFooter'
import { useReconciliationSuggestion } from 'modules/banking/hooks/useReconciliationSuggestions'
import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

type Props = {
  transaction: TransactionReconciliationQuery['banking']['transaction']
  onTransactionFullyReconciled: () => void
  onHideSuggestions?: () => void
}

export const LineItem = ({
  label,
  value,
  textAlign,
}: {
  label: string
  value: string | number | React.ReactNode
  textAlign: 'center' | 'left' | 'right'
}) => (
  <Box gap="s2" textAlign={textAlign}>
    <Text.Normal color="grey1000" weight="semibold" size="200">
      {label}
    </Text.Normal>
    <Text.Normal color="grey800" weight="medium" size="200">
      {value}
    </Text.Normal>
  </Box>
)

const Suggestion = ({
  invoice,
  paymentExecution,
  suggestionId,
  currency,
  locale,
}: {
  paymentExecution?: ReconciliationSuggestionPaymentExecutionFragment | null
  invoice?: ReconciliationSuggestionInvoiceFragment | null
  suggestionId: number
  currency: string
  locale: string
}) => {
  if (invoice) {
    return (
      <InvoiceSuggestion
        invoice={invoice}
        suggestionId={suggestionId}
        currency={currency}
        locale={locale}
      />
    )
  } else {
    if (paymentExecution) {
      return (
        <PaymentExecutionSuggestion
          paymentExecution={paymentExecution}
          suggestionId={suggestionId}
          currency={currency}
          locale={locale}
        />
      )
    } else {
      return null
    }
  }
}
const PaymentExecutionSuggestion = ({
  paymentExecution,
  suggestionId,
  currency,
  locale,
}: {
  paymentExecution: ReconciliationSuggestionPaymentExecutionFragment
  suggestionId: number
  currency: string
  locale: string
}) => {
  const { navigation } = useNavigation()
  return (
    <Box key={suggestionId} gap="s12">
      <Box justifyContent="spaceBetween" flexDirection="row">
        <LineItem
          label={i18n.t('banking.reconciliations.suggestions.payment')}
          value={paymentExecution.payment.label || paymentExecution.payment.description}
          textAlign="left"
        />
        <LineItem
          label={i18n.t('banking.reconciliations.suggestions.amount')}
          value={formattedAmount({
            amountCents: Number(paymentExecution.amountCents),
            currency,
            locale,
          })}
          textAlign="right"
        />
      </Box>
      <Box justifyContent="spaceBetween" flexDirection="row">
        <LineItem
          label={i18n.t('banking.reconciliations.suggestions.sepa_generated_at')}
          value={dateBackendToUI(String(paymentExecution.sepaGeneratedAt))}
          textAlign="left"
        />
        <LineItem
          label={i18n.t('banking.reconciliations.suggestions.transfers_count')}
          value={paymentExecution.transfersConnection.totalCount}
          textAlign="right"
        />
      </Box>
      <Box
        aria-label={i18n.t('banking.reconciliations.suggestions.view_payment')}
        flexDirection="row"
        alignItems="center"
        gap="s4"
      >
        <Link.Router
          to={navigation.finance.banking.payments.paymentId.fullPath({
            id: paymentExecution.payment.id,
          })}
        >
          <Text.Normal color="grey800" size="200" weight="medium">
            {i18n.t('banking.reconciliations.suggestions.view_payment')}
          </Text.Normal>
        </Link.Router>
        <Icon.Small icon="arrow-right" color="grey800" />
      </Box>
    </Box>
  )
}

const InvoiceSuggestion = ({
  invoice,
  suggestionId,
  currency,
  locale,
}: {
  invoice: ReconciliationSuggestionInvoiceFragment
  suggestionId: number
  currency: string
  locale: string
}) => {
  const { navigation } = useNavigation()
  return (
    <Box key={suggestionId} gap="s12">
      <Box justifyContent="spaceBetween" flexDirection="row">
        <LineItem
          label={i18n.t('banking.reconciliations.suggestions.invoice_number')}
          value={invoice.documentNumber}
          textAlign="left"
        />
        <LineItem
          label={i18n.t('banking.reconciliations.suggestions.amount')}
          value={formattedAmount({
            amountCents: Number(invoice.totalAmountCents),
            currency,
            locale,
          })}
          textAlign="right"
        />
      </Box>
      <Box justifyContent="spaceBetween" flexDirection="row">
        <LineItem
          label={i18n.t('banking.reconciliations.suggestions.date')}
          value={dateBackendToUI(String(invoice.documentDate))}
          textAlign="left"
        />
        <LineItem
          label={i18n.t('banking.reconciliations.suggestions.vendor')}
          value={
            invoice.vendor?.legalName ||
            i18n.t('banking.reconciliations.suggestions.missing_vendor')
          }
          textAlign="right"
        />
      </Box>
      <Box justifyContent="spaceBetween" flexDirection="row">
        <LineItem
          label={i18n.t('banking.reconciliations.suggestions.due_date')}
          value={dateBackendToUI(String(invoice.dueDate || invoice.documentDate))}
          textAlign="left"
        />
        {(invoice.taxes.length ?? 0) > 0 && (
          <Box gap="s2" textAlign="right">
            <Text.Normal color="grey1000" weight="semibold" size="200">
              {i18n.t('banking.reconciliations.suggestions.taxes')}
            </Text.Normal>
            <Box gap="s2" flexDirection="row">
              {invoice.taxes.map((tax) => (
                <Box key={`${tax.amountCents}${tax.percentage}`} flexDirection="row" gap="s2">
                  {tax.amountCents && (
                    <Text.Normal>
                      {formattedAmount({
                        amountCents: tax.amountCents,
                        currency,
                        locale,
                      })}
                    </Text.Normal>
                  )}
                  {tax.amountCents && tax.percentage && (
                    <Text.Normal>({tax.percentage * 100}%)</Text.Normal>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
      <Box
        aria-label={i18n.t('banking.reconciliations.suggestions.view_invoice')}
        flexDirection="row"
        alignItems="center"
        gap="s4"
      >
        <Link.Router
          to={navigation.finance.invoices.list.invoiceId.fullPath({
            invoiceId: invoice?.id + '',
          })}
        >
          <Text.Normal color="grey800" size="200" weight="medium">
            {i18n.t('banking.reconciliations.suggestions.view_invoice')}
          </Text.Normal>
        </Link.Router>
        <Icon.Small icon="arrow-right" color="grey800" />
      </Box>
    </Box>
  )
}

export const ReconciliationSuggestions = ({
  transaction,
  onTransactionFullyReconciled,
  onHideSuggestions,
}: Props) => {
  const [showSuggestion, setShowSuggestion] = useState(false)
  const { access, company } = useCurrentSession()
  const [currentIndex, setCurrentIndex] = useState(0)

  const suggestions = transaction?.reconciliationSuggestions
  const invoice = suggestions && suggestions[0]?.financialDocument
  const paymentExecution = suggestions && suggestions[0]?.paymentExecution

  const { isLoading, firstSuggestion, confirmSuggestion, rejectSuggestion } =
    useReconciliationSuggestion({
      suggestion: suggestions && suggestions[0],
      companyId: company.id,
      onTransactionFullyReconciled: onTransactionFullyReconciled,
    })

  setTimeout(() => {
    setShowSuggestion(true)
  }, 100)

  const handleReject = useUnsafeCallback(() => {
    track(trackingEvents.accounting.banking.clickRejectReconciliationSuggestion)
    setCurrentIndex((curr) => (curr > 0 ? curr - 1 : 0))
    rejectSuggestion()
  }, [rejectSuggestion])

  const actionButtons = useUnsafeMemo(
    () => (
      <Box flexDirection="row" gap="s6">
        <Button type="secondary" onClick={handleReject} disabled={isLoading}>
          {i18n.t('banking.reconciliations.suggestions.reject')}
        </Button>
        <Button
          onClick={() => {
            track(trackingEvents.accounting.banking.clickAcceptReconciliationSuggestion)
            confirmSuggestion()
          }}
          disabled={isLoading}
        >
          {i18n.t('banking.reconciliations.suggestions.confirm')}
        </Button>
      </Box>
    ),
    [confirmSuggestion, isLoading, handleReject]
  )

  if (!suggestions || (!invoice && !paymentExecution) || !showSuggestion) return null

  const currency = invoice?.currency || paymentExecution?.currency || ''
  const locale = access.locale

  const contentAnimationVariants = {
    enter: () => {
      if (firstSuggestion) {
        return {
          opacity: 0,
          translateY: '-100%',
        }
      }
      return {
        opacity: 1,
        translateY: 0,
      }
    },
    show: {
      opacity: 1,
      translateY: 0,
      translateX: 0,
    },
  }
  return (
    <Box paddingX="s16" paddingBottom="s16" gap="s16">
      <Box flexDirection="row" width="full" alignItems="center" justifyContent="spaceBetween">
        <Box flexDirection="row" gap="s4">
          <Text.Normal weight="semibold">
            {i18n.t('banking.reconciliations.suggestions.title')}
          </Text.Normal>
          <Icon.Medium icon="ai" color="grey800" />
        </Box>
        {onHideSuggestions && (
          <Link.Touchable onClick={onHideSuggestions}>
            {i18n.t('banking.reconciliations.suggestions.dismiss')}
          </Link.Touchable>
        )}
      </Box>

      <Text.Normal>{i18n.t('banking.reconciliations.suggestions.subtitle')}</Text.Normal>
      <Box overflowY="hidden">
        <Box
          background="white"
          paddingX="s24"
          paddingY="s16"
          flexDirection="row"
          gap="s32"
          style={{
            border: '1px solid #e2e2e5',
            borderRadius: '16px',
            zIndex: '99',
          }}
        >
          <Box>
            <Text.Normal color="grey800" size="200">
              {dateBackendDateTimeToUI(transaction.bookingDate)}
            </Text.Normal>
            <Text.Normal color="grey800" size="200">
              {formattedAmount({
                amountCents: transaction.amountCents,
                currency,
                locale,
              })}
            </Text.Normal>
          </Box>
          <Box>
            <Text.Normal color="grey1000" size="200" weight="medium">
              {transaction.description}
            </Text.Normal>
          </Box>
        </Box>
        <AnimatePresence mode="popLayout">
          <Box.Motion
            flexGrow
            paddingTop="s0"
            initial="enter"
            animate="show"
            exit="exit"
            variants={contentAnimationVariants}
            transition={{ easing: 'easeInOut', duration: 0.5 }}
            maxHeight="full"
          >
            <Box
              gap="s16"
              background="grey100"
              style={{
                border: '1px solid #e2e2e5',
                borderBottomLeftRadius: '16px',
                borderBottomRightRadius: '16px',
                borderTop: 'none',
                marginTop: '-15px',
                padding: '32px 24px 16px 24px',
              }}
            >
              <Carousel
                index={currentIndex}
                footer={
                  <CarouselFooter
                    total={suggestions.length}
                    currentIndex={currentIndex}
                    setCurrentIndex={setCurrentIndex}
                    actionButtonsComponent={actionButtons}
                  />
                }
              >
                {suggestions.map((suggestion) => (
                  <Suggestion
                    key={suggestion.id}
                    suggestionId={suggestion.id}
                    currency={currency}
                    locale={locale}
                    invoice={invoice}
                    paymentExecution={paymentExecution}
                  />
                ))}
              </Carousel>
            </Box>
          </Box.Motion>
        </AnimatePresence>
      </Box>
    </Box>
  )
}
